import React, { useRef, useEffect } from "react";
import { PopoutHeader } from "./popout-header";
import { createPortal } from "react-dom";

import "./popout.scss";

export const Popout = ({ id, togglePopout, title, classes, isInModal, onPopoutResize, children }) => {
    const popoutRef = useRef();
    const portalRoot = isInModal ? "modal-root" : "root";
    const documentRoot = document.getElementById(portalRoot);

    useEffect(() => {
        let resizeObserverValue = popoutRef.current;
        const resizeable = resizeObserverValue;

        setInitialPos();

        window.addEventListener("resize", handleReposition);
        resizeObserver.observe(resizeObserverValue);
        resizeable.addEventListener("resize", handleResize);
        popoutResizeObserver.observe(resizeable);

        return () => {
            resizeObserver.unobserve(resizeObserverValue);
            window.removeEventListener("resize", handleReposition);
            popoutResizeObserver.unobserve(resizeable);
            resizeable.removeEventListener("resize", handleResize);
        };
    }, []);

    const getWindowBox = () => {
        return {
            left: 0,
            right: Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
            top: 0,
            bottom: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
        };
    };

    const setInitialPos = () => {
        const wRect = getWindowBox();
        const tgtRect = popoutRef.current.getBoundingClientRect();
        const scrollBarWidth = window.innerWidth - document.body.clientWidth;
        const translateX = wRect.right / 2 - tgtRect.width / 2 - scrollBarWidth + "px"; //1/2 of window width minus 1/2 of element width minus scrollbar to center element on x-axis
        const translateY = wRect.bottom / 2 - tgtRect.height / 2 + "px"; //1/2 of window height minus 1/2 of element height to center element on y-axis
        Object.assign(popoutRef.current.style, { transform: `translateX(${translateX}) translateY(${translateY})` });
    };

    const handleReposition = (e) => {
        if (!popoutRef || !popoutRef.current) {
            return;
        }

        const wRect = getWindowBox();
        const tgtRect = popoutRef.current.getBoundingClientRect();
        const scrollBarWidth = window.innerWidth - document.body.clientWidth;

        let translateX = tgtRect.left + "px";
        let translateY = tgtRect.top + "px";

        if (tgtRect.left + tgtRect.width > wRect.right) {
            translateX = wRect.right - tgtRect.width - scrollBarWidth - 20 + "px";
        }

        if (tgtRect.top + tgtRect.height > wRect.bottom) {
            translateY = wRect.bottom - tgtRect.height - 20 + "px";
        }

        Object.assign(popoutRef.current.style, { transform: `translateX(${translateX}) translateY(${translateY})` });
    };

    const handleResize = () => {
        if (!popoutRef || !popoutRef.current) {
            return;
        }

        const wRect = getWindowBox();

        Object.assign(popoutRef.current.style, { maxWidth: `${wRect.right - 50}px`, maxHeight: `${wRect.bottom - 50}px` });

        return onPopoutResize();
    };

    const resizeObserver = new ResizeObserver(handleReposition);
    const popoutResizeObserver = new ResizeObserver(() => {
        // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
        requestAnimationFrame(() => {
            return handleResize();
        });
    });

    const handleFocus = () => {
        const popoutList = document.querySelectorAll(".popout-container");

        popoutList.forEach((popout) => {
            popout.classList.remove("-selected");
        });
        popoutRef.current.classList.add("-selected");
    };

    return createPortal(
        <>
            <div
                ref={popoutRef}
                id={id}
                className={`popout-container ${classes}`}
                onClick={handleFocus}
            >
                <PopoutHeader
                    togglePopout={togglePopout}
                    getWindowBox={getWindowBox}
                    title={title}
                    parentId={id}
                />
                {children}
            </div>
        </>,
        documentRoot
    );
};
